import React from "react";

/**
 * Shared hero band for every marketing sub-page.
 *
 *   <MarketingPageHero
 *     eyebrow="Pricing"
 *     title={<>Pay per scan.<br/>Not per seat.</>}
 *     subtitle="Optional supporting line."
 *   >
 *     {optional CTA button}
 *   </MarketingPageHero>
 *
 * Uses the same `/hero-bg.jpg` treatment as the homepage Hero so every marketing
 * page opens with the same visual mood. All spacing (vertical padding, gap
 * between title/subtitle/CTA) is locked in here — pages should NOT override it,
 * so consecutive routes feel identical when you click between them.
 */
export default function MarketingPageHero({ eyebrow, title, subtitle, children }) {
  return (
    <section className="relative overflow-hidden" data-testid="marketing-page-hero">
      {/* Same background image as the homepage hero */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: "url('/hero-bg.jpg')" }}
        aria-hidden="true"
      />
      {/* Dark overlay for text legibility */}
      <div className="absolute inset-0 pointer-events-none bg-black/45" aria-hidden="true" />

      <div className="relative max-w-5xl mx-auto px-5 md:px-10 pt-16 md:pt-24 pb-14 md:pb-20 text-center">
        {eyebrow && (
          <div className="text-[11px] font-mono uppercase tracking-widest text-lm-brand-green">
            {eyebrow}
          </div>
        )}
        <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-display font-semibold tracking-tight text-white leading-[1.05]">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-5 text-base md:text-lg text-white/80 max-w-2xl mx-auto leading-relaxed">
            {subtitle}
          </p>
        )}
        {children && <div className="mt-8 flex flex-wrap items-center justify-center gap-3">{children}</div>}
      </div>
    </section>
  );
}
